/**
 * Character Limits Configuration for PageBuilder AI Generation
 * 
 * This file defines character limits for all PageBuilder sections to ensure
 * AI-generated content fits within UI design constraints and maintains
 * optimal user experience across different screen sizes and languages.
 * 
 * Limits are based on:
 * - UI component design constraints
 * - Readability and user experience
 * - Mobile responsiveness requirements
 * - Multi-language support (RTL/LTR)
 */

/**
 * Core character limits for PageBuilder sections
 */
export interface PageBuilderCharacterLimits {
  // Hero Section - Main landing area with primary CTA
  heroTitle: 60;           // Large heading, needs to fit on mobile without wrapping
  heroSubtitle: 120;       // Supporting text, 2-3 lines max on mobile
  heroHighlight: 50;       // Bullet points, should be concise and punchy
  
  // Project Overview - Core project information
  projectDescription: 500;     // Main project description, paragraph format
  projectTitle: 80;            // Project name/title
  sellingPointTitle: 40;       // Key selling point headlines
  sellingPointDescription: 150; // Supporting details for selling points
  
  // Unit Types - Property specifications
  unitTypeDescription: 100;    // Type description (studio, 1BR, etc.)
  unitTypeSize: 20;           // Size specification (e.g., "850 sq ft")
  unitTypeStartingPrice: 30;  // Price display (e.g., "Starting from AED 1.2M")
  
  // Features & Highlights - Amenities and benefits
  featureTitle: 30;           // Feature/amenity name
  featureDescription: 100;    // Feature description/benefit
  
  // Developer Information
  developerName: 50;          // Developer company name
  developerDescription: 300;  // Developer background and credibility
  
  // FAQ Section - Frequently asked questions
  faqQuestion: 80;            // Question text, should be clear and concise
  faqAnswer: 300;             // Answer text, comprehensive but not overwhelming
  
  // Payment Plans - Financial information
  paymentPlanTitle: 50;           // Overall payment section title
  paymentPlanDescription: 200;    // Payment section description
  paymentPlanName: 40;            // Individual plan name (e.g., "Easy Plan")
  paymentPlanPlanDescription: 150; // Individual plan description
  milestoneDescription: 150;      // Construction milestone description
  paymentHighlightTitle: 40;      // Payment benefit title
  paymentHighlightDescription: 100; // Payment benefit description
  
  // Location & Connectivity
  locationProjectName: 60;        // Project name in location context
  nearbyPlaceName: 50;           // Nearby place name
  nearbyPlaceDescription: 100;   // Why this nearby place matters
  locationBenefit: 150;          // Location advantage description
  keyDistancePlace: 40;          // Destination name
  keyDistanceTime: 20;           // Travel time (e.g., "10 minutes")
  
  // Gallery & Media
  galleryImageAlt: 80;           // Image alt text for accessibility
  galleryImageCategory: 20;      // Image category (interior, exterior, etc.)
  
  // Lead Form - Contact form configuration
  leadFormTitle: 60;                    // Form main title
  leadFormDescription: 150;             // Form introduction text
  leadFormStepTitle: 40;               // Individual step title
  leadFormStepDescription: 100;        // Step description
  leadFormLabel: 30;                   // Field label
  leadFormOptionLabel: 40;             // Dropdown/radio option labels
  leadFormValidationMessage: 60;       // Error message text
  leadFormSuccessTitle: 50;            // Success page title
  leadFormSuccessDescription: 120;     // Success page description
  
  // Download Form - Brochure download
  downloadFormTitle: 60;               // Download form title
  downloadFormSubtitle: 100;          // Download form subtitle
  downloadFormBenefit: 120;           // Benefit of downloading brochure
  downloadFormWelcomeTitle: 50;       // Welcome message title
  downloadFormWelcomeDescription: 150; // Welcome message description
  
  // Email Templates - Automated communications
  emailSubject: 80;                   // Email subject line
  emailContent: 2000;                 // Email body content
  emailTextContent: 1500;             // Plain text version
  
  // Legal Information
  legalTitle: 50;                     // Legal section title
  legalContent: 1000;                 // Legal disclaimer content
  legalDisclaimer: 200;               // Short legal notice
}

/**
 * Field type enumeration for better type safety and AI prompt configuration
 */
export enum FieldType {
  // Hero Section
  HERO_TITLE = 'heroTitle',
  HERO_SUBTITLE = 'heroSubtitle', 
  HERO_HIGHLIGHT = 'heroHighlight',
  
  // Project Overview
  PROJECT_DESCRIPTION = 'projectDescription',
  PROJECT_TITLE = 'projectTitle',
  SELLING_POINT_TITLE = 'sellingPointTitle',
  SELLING_POINT_DESCRIPTION = 'sellingPointDescription',
  
  // Unit Types
  UNIT_TYPE_DESCRIPTION = 'unitTypeDescription',
  UNIT_TYPE_SIZE = 'unitTypeSize',
  UNIT_TYPE_STARTING_PRICE = 'unitTypeStartingPrice',
  
  // Features
  FEATURE_TITLE = 'featureTitle',
  FEATURE_DESCRIPTION = 'featureDescription',
  
  // Developer
  DEVELOPER_NAME = 'developerName',
  DEVELOPER_DESCRIPTION = 'developerDescription',
  
  // FAQ
  FAQ_QUESTION = 'faqQuestion',
  FAQ_ANSWER = 'faqAnswer',
  
  // Payment Plans
  PAYMENT_PLAN_TITLE = 'paymentPlanTitle',
  PAYMENT_PLAN_DESCRIPTION = 'paymentPlanDescription',
  PAYMENT_PLAN_NAME = 'paymentPlanName',
  PAYMENT_PLAN_PLAN_DESCRIPTION = 'paymentPlanPlanDescription',
  MILESTONE_DESCRIPTION = 'milestoneDescription',
  PAYMENT_HIGHLIGHT_TITLE = 'paymentHighlightTitle',
  PAYMENT_HIGHLIGHT_DESCRIPTION = 'paymentHighlightDescription',
  
  // Location
  LOCATION_PROJECT_NAME = 'locationProjectName',
  NEARBY_PLACE_NAME = 'nearbyPlaceName',
  NEARBY_PLACE_DESCRIPTION = 'nearbyPlaceDescription',
  LOCATION_BENEFIT = 'locationBenefit',
  KEY_DISTANCE_PLACE = 'keyDistancePlace',
  KEY_DISTANCE_TIME = 'keyDistanceTime',
  
  // Gallery
  GALLERY_IMAGE_ALT = 'galleryImageAlt',
  GALLERY_IMAGE_CATEGORY = 'galleryImageCategory',
  
  // Lead Form
  LEAD_FORM_TITLE = 'leadFormTitle',
  LEAD_FORM_DESCRIPTION = 'leadFormDescription',
  LEAD_FORM_STEP_TITLE = 'leadFormStepTitle',
  LEAD_FORM_STEP_DESCRIPTION = 'leadFormStepDescription',
  LEAD_FORM_LABEL = 'leadFormLabel',
  LEAD_FORM_OPTION_LABEL = 'leadFormOptionLabel',
  LEAD_FORM_VALIDATION_MESSAGE = 'leadFormValidationMessage',
  LEAD_FORM_SUCCESS_TITLE = 'leadFormSuccessTitle',
  LEAD_FORM_SUCCESS_DESCRIPTION = 'leadFormSuccessDescription',
  
  // Download Form
  DOWNLOAD_FORM_TITLE = 'downloadFormTitle',
  DOWNLOAD_FORM_SUBTITLE = 'downloadFormSubtitle',
  DOWNLOAD_FORM_BENEFIT = 'downloadFormBenefit',
  DOWNLOAD_FORM_WELCOME_TITLE = 'downloadFormWelcomeTitle',
  DOWNLOAD_FORM_WELCOME_DESCRIPTION = 'downloadFormWelcomeDescription',
  
  // Email Templates
  EMAIL_SUBJECT = 'emailSubject',
  EMAIL_CONTENT = 'emailContent',
  EMAIL_TEXT_CONTENT = 'emailTextContent',
  
  // Legal
  LEGAL_TITLE = 'legalTitle',
  LEGAL_CONTENT = 'legalContent',
  LEGAL_DISCLAIMER = 'legalDisclaimer'
}

/**
 * Default character limits configuration
 * These limits ensure content fits well in UI components and provides good UX
 */
export const CHARACTER_LIMITS: PageBuilderCharacterLimits = {
  // Hero Section
  heroTitle: 60,
  heroSubtitle: 120,
  heroHighlight: 50,
  
  // Project Overview
  projectDescription: 500,
  projectTitle: 80,
  sellingPointTitle: 40,
  sellingPointDescription: 150,
  
  // Unit Types
  unitTypeDescription: 100,
  unitTypeSize: 20,
  unitTypeStartingPrice: 30,
  
  // Features & Highlights
  featureTitle: 30,
  featureDescription: 100,
  
  // Developer
  developerName: 50,
  developerDescription: 300,
  
  // FAQ Section
  faqQuestion: 80,
  faqAnswer: 300,
  
  // Payment Plans
  paymentPlanTitle: 50,
  paymentPlanDescription: 200,
  paymentPlanName: 40,
  paymentPlanPlanDescription: 150,
  milestoneDescription: 150,
  paymentHighlightTitle: 40,
  paymentHighlightDescription: 100,
  
  // Location
  locationProjectName: 60,
  nearbyPlaceName: 50,
  nearbyPlaceDescription: 100,
  locationBenefit: 150,
  keyDistancePlace: 40,
  keyDistanceTime: 20,
  
  // Gallery
  galleryImageAlt: 80,
  galleryImageCategory: 20,
  
  // Lead Form
  leadFormTitle: 60,
  leadFormDescription: 150,
  leadFormStepTitle: 40,
  leadFormStepDescription: 100,
  leadFormLabel: 30,
  leadFormOptionLabel: 40,
  leadFormValidationMessage: 60,
  leadFormSuccessTitle: 50,
  leadFormSuccessDescription: 120,
  
  // Download Form
  downloadFormTitle: 60,
  downloadFormSubtitle: 100,
  downloadFormBenefit: 120,
  downloadFormWelcomeTitle: 50,
  downloadFormWelcomeDescription: 150,
  
  // Email Templates
  emailSubject: 80,
  emailContent: 2000,
  emailTextContent: 1500,
  
  // Legal
  legalTitle: 50,
  legalContent: 1000,
  legalDisclaimer: 200
};

/**
 * Get character limit for a specific field type
 * @param fieldType - The field type to get limit for
 * @returns Character limit for the field
 */
export const getCharacterLimit = (fieldType: FieldType): number => {
  return CHARACTER_LIMITS[fieldType as keyof PageBuilderCharacterLimits];
};

/**
 * Validate if text exceeds character limit for a field type
 * @param text - Text to validate
 * @param fieldType - Field type to check against
 * @returns Object with validation result and details
 */
export const validateCharacterLimit = (text: string, fieldType: FieldType): {
  isValid: boolean;
  currentLength: number;
  maxLength: number;
  remainingChars: number;
  exceedsBy: number;
} => {
  const maxLength = getCharacterLimit(fieldType);
  const currentLength = text.length;
  const remainingChars = Math.max(0, maxLength - currentLength);
  const exceedsBy = Math.max(0, currentLength - maxLength);
  
  return {
    isValid: currentLength <= maxLength,
    currentLength,
    maxLength,
    remainingChars,
    exceedsBy
  };
};

/**
 * Get character limit warning threshold (80% of limit)
 * @param fieldType - Field type to check
 * @returns Warning threshold character count
 */
export const getWarningThreshold = (fieldType: FieldType): number => {
  return Math.floor(getCharacterLimit(fieldType) * 0.8);
};

/**
 * Check if text is approaching character limit (within warning threshold)
 * @param text - Text to check
 * @param fieldType - Field type to check against
 * @returns True if approaching limit
 */
export const isApproachingLimit = (text: string, fieldType: FieldType): boolean => {
  return text.length >= getWarningThreshold(fieldType);
};

/**
 * Project type variations that may affect character limits in the future
 * Currently using default limits for all project types
 */
export enum ProjectType {
  LUXURY = 'luxury',
  AFFORDABLE = 'affordable', 
  COMMERCIAL = 'commercial',
  MIXED_USE = 'mixedUse'
}

/**
 * Language-specific character limit adjustments
 * Some languages may need different limits due to character density
 */
export const LANGUAGE_MULTIPLIERS = {
  en: 1.0,      // English baseline
  ar: 0.8,      // Arabic - denser script, may need shorter limits
  fa: 0.8,      // Persian - similar to Arabic
  ur: 0.8,      // Urdu - similar density
  hi: 0.9,      // Hindi - moderate adjustment
  // Add more languages as needed
};

/**
 * Get character limit adjusted for language
 * @param fieldType - Field type
 * @param languageCode - Language code (e.g., 'en', 'ar')
 * @returns Adjusted character limit
 */
export const getLanguageAdjustedLimit = (fieldType: FieldType, languageCode: string = 'en'): number => {
  const baseLimit = getCharacterLimit(fieldType);
  const multiplier = LANGUAGE_MULTIPLIERS[languageCode as keyof typeof LANGUAGE_MULTIPLIERS] || 1.0;
  return Math.floor(baseLimit * multiplier);
};

export default CHARACTER_LIMITS; 